/**
 * LimitReachedModal — Shows when user hits a plan limit
 * Prompts upgrade with link to subscription page or /pricing
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faArrowUp,
  faTimes,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import '../styles/LimitReachedModal.css';

interface LimitReachedModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: 'items' | 'storage' | 'images' | 'workspaces' | 'members' | 'categories' | 'ai_messages' | 'generic';
  currentUsage?: number;
  maxAllowed?: number;
  planName?: string;
}

const LIMIT_MESSAGES: Record<string, { title: string; message: string; icon: string }> = {
  items: {
    title: 'Item Limit Reached',
    message: 'You\'ve reached the maximum number of items for your current plan. Upgrade to add more items to your collection.',
    icon: '📦',
  },
  storage: {
    title: 'Storage Limit Reached',
    message: 'Your storage is full. Upgrade your plan or contact us for additional storage options.',
    icon: '💾',
  },
  images: {
    title: 'Image Limit Reached',
    message: 'You\'ve reached the maximum number of images per item for your current plan. Upgrade to upload more photos.',
    icon: '📸',
  },
  workspaces: {
    title: 'Workspace Limit Reached',
    message: 'You\'ve reached the maximum number of workspaces for your current plan. Upgrade to create more workspaces.',
    icon: '🏢',
  },
  members: {
    title: 'Member Limit Reached',
    message: 'You\'ve reached the maximum number of members for your workspace. Upgrade to invite more team members.',
    icon: '👥',
  },
  categories: {
    title: 'Category Limit Reached',
    message: 'You\'ve reached the maximum number of categories for your current plan. Upgrade to create more categories.',
    icon: '🏷️',
  },
  ai_messages: {
    title: 'AI Message Limit Reached',
    message: 'You\'ve used all your AI messages for this month. Upgrade your plan for more AI interactions.',
    icon: '🤖',
  },
  generic: {
    title: 'Plan Limit Reached',
    message: 'You\'ve reached a limit of your current plan. Upgrade to unlock more features and capacity.',
    icon: '⚡',
  },
};

const LimitReachedModal = ({
  isOpen,
  onClose,
  limitType,
  currentUsage,
  maxAllowed,
  planName = 'Free',
}: LimitReachedModalProps) => {
  const navigate = useNavigate();
  const info = LIMIT_MESSAGES[limitType] || LIMIT_MESSAGES.generic;

  if (!isOpen) return null;

  return (
    <div className="limit-modal-overlay" onClick={onClose}>
      <div className="limit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="limit-modal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="limit-modal-icon">
          <span>{info.icon}</span>
          <FontAwesomeIcon icon={faExclamationTriangle} className="warning-badge" />
        </div>

        <h2>{info.title}</h2>
        <p className="limit-modal-message">{info.message}</p>

        {currentUsage !== undefined && maxAllowed !== undefined && (
          <div className="limit-modal-usage">
            <div className="usage-bar">
              <div className="usage-fill" style={{ width: '100%' }} />
            </div>
            <span className="usage-text">
              {currentUsage} / {maxAllowed === -1 ? '∞' : maxAllowed} used
            </span>
          </div>
        )}

        <div className="limit-modal-plan-info">
          <span>Current plan: <strong>{planName}</strong></span>
        </div>

        <div className="limit-modal-actions">
          <button
            className="limit-btn-upgrade"
            onClick={() => { onClose(); navigate('/subscription'); }}
          >
            <FontAwesomeIcon icon={faArrowUp} /> Upgrade Plan
          </button>
          <button
            className="limit-btn-pricing"
            onClick={() => { onClose(); navigate('/subscription'); }}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} /> View All Plans
          </button>
        </div>

        <p className="limit-modal-footer">
          Upgrade anytime — your data is always safe.
        </p>
      </div>
    </div>
  );
};

export default LimitReachedModal;
